import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Save, X, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import StateDropdown from '../../components/forms/StateDropdown';
import SelectField from '../../components/ui/SelectField';

const CollegeForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    short_name: '',
    location: '',
    state: '',
    type: 'Public',
    enrollment: 0,
    acceptance_rate: 0,
    tuition_in_state: 0,
    tuition_out_state: 0,
    sat_range: '',
    act_range: '',
    graduation_rate: 0,
    ranking: null,
    rating: '',
    image: '',
    description: '',
    direct_admission: false,
    majors: [],
    features: [],
    // Images (ImageKit paths or full URLs)
    hero_url: '',
    logo_url: '',
    banner_url: '',
    // Social / contact links
    instagram: '',
    twitter: '',
    facebook: '',
    youtube: '',
    linkedin: '',
    virtual_tour_url: '',
    // Settings
    is_featured: false,
    is_partner: false,
  });

  const [majorsInput, setMajorsInput] = useState('');
  const [featuresInput, setFeaturesInput] = useState('');

  const loadCollege = useCallback(async () => {
    try {
      const response = await api.get(`/api/colleges/${id}`);
      const college = response.data;
      // Merge nested IPEDS paths (if present) into the flat admin form shape.
      const merged = {
        ...college,
        majors: college.majors || [],
        features: college.features || [],
        hero_url: college.hero_url ?? college.images?.heroUrl ?? '',
        logo_url: college.logo_url ?? college.images?.logoUrl ?? '',
        banner_url: college.banner_url ?? college.images?.bannerUrl ?? '',
        instagram: college.instagram ?? college.contact?.instagram ?? '',
        twitter: college.twitter ?? college.contact?.twitter ?? '',
        facebook: college.facebook ?? college.contact?.facebook ?? '',
        youtube: college.youtube ?? college.contact?.youtube ?? '',
        linkedin: college.linkedin ?? college.contact?.linkedin ?? '',
        virtual_tour_url: college.virtual_tour_url ?? college.contact?.virtualTourUrl ?? '',
        is_featured: college.is_featured ?? college.flags?.isFeatured ?? false,
        is_partner: college.is_partner ?? college.flags?.isPartner ?? false,
      };
      setFormData(merged);
      setMajorsInput((merged.majors || []).join(', '));
      setFeaturesInput((merged.features || []).join(', '));
    } catch (error) {
      console.error('Failed to load college:', error);
      toast.error('Failed to load college data');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (isEdit) {
      loadCollege();
    }
  }, [isEdit, loadCollege]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Parse majors and features from comma-separated strings
      const dataToSubmit = {
        ...formData,
        majors: majorsInput.split(',').map((m) => m.trim()).filter(Boolean),
        features: featuresInput.split(',').map((f) => f.trim()).filter(Boolean),
        enrollment: parseInt(formData.enrollment) || 0,
        acceptance_rate: parseFloat(formData.acceptance_rate) || 0,
        tuition_in_state: parseInt(formData.tuition_in_state) || 0,
        tuition_out_state: parseInt(formData.tuition_out_state) || 0,
        graduation_rate: parseFloat(formData.graduation_rate) || 0,
        ranking: formData.ranking ? parseInt(formData.ranking) : null,
        // Images / social / settings (backend maps to images.*, contact.*, flags.*)
        hero_url: formData.hero_url || null,
        logo_url: formData.logo_url || null,
        banner_url: formData.banner_url || null,
        instagram: formData.instagram || null,
        twitter: formData.twitter || null,
        facebook: formData.facebook || null,
        youtube: formData.youtube || null,
        linkedin: formData.linkedin || null,
        virtual_tour_url: formData.virtual_tour_url || null,
        is_featured: Boolean(formData.is_featured),
        is_partner: Boolean(formData.is_partner),
      };

      if (isEdit) {
        await api.put(`/api/admin/colleges/${id}`, dataToSubmit);
        toast.success('College updated successfully');
      } else {
        await api.post('/api/admin/colleges', dataToSubmit);
        toast.success('College created successfully');
      }

      navigate('/admin/colleges');
    } catch (error) {
      console.error('Failed to save college:', error);
      toast.error('Failed to save college. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/admin/colleges"
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft size={24} />
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {isEdit ? 'Edit College' : 'Add New College'}
          </h1>
          <p className="text-gray-600 mt-1">
            {isEdit ? 'Update college information' : 'Create a new college entry'}
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm p-8 space-y-8">
        {/* Basic Information */}
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-4">Basic Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                College Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-4 focus:ring-green-100 transition-all outline-none"
                placeholder="e.g., Stanford University"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Short Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="short_name"
                value={formData.short_name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-4 focus:ring-green-100 transition-all outline-none"
                placeholder="e.g., Stanford"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Location <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-4 focus:ring-green-100 transition-all outline-none"
                placeholder="e.g., Stanford, CA"
              />
            </div>

            <div>
              <StateDropdown
                value={formData.state}
                onChange={(value) => setFormData({ ...formData, state: value })}
                label="State"
                required
                className="mt-0"
              />
            </div>

            <div>
              <SelectField
                label="Type"
                value={formData.type}
                onChange={(value) => setFormData(prev => ({ ...prev, type: value }))}
                options={['Public', 'Private']}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Image URL
              </label>
              <input
                type="url"
                name="image"
                value={formData.image}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-4 focus:ring-green-100 transition-all outline-none"
                placeholder="https://example.com/image.jpg"
              />
            </div>
          </div>
        </div>

        {/* Statistics */}
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-4">Statistics</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Enrollment
              </label>
              <input
                type="number"
                name="enrollment"
                value={formData.enrollment}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-4 focus:ring-green-100 transition-all outline-none"
                placeholder="e.g., 17000"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Acceptance Rate (%)
              </label>
              <input
                type="number"
                step="0.1"
                name="acceptance_rate"
                value={formData.acceptance_rate}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-4 focus:ring-green-100 transition-all outline-none"
                placeholder="e.g., 5.2"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Graduation Rate (%)
              </label>
              <input
                type="number"
                step="0.1"
                name="graduation_rate"
                value={formData.graduation_rate}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-4 focus:ring-green-100 transition-all outline-none"
                placeholder="e.g., 94.0"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Tuition In-State ($)
              </label>
              <input
                type="number"
                name="tuition_in_state"
                value={formData.tuition_in_state}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-4 focus:ring-green-100 transition-all outline-none"
                placeholder="e.g., 18000"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Tuition Out-of-State ($)
              </label>
              <input
                type="number"
                name="tuition_out_state"
                value={formData.tuition_out_state}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-4 focus:ring-green-100 transition-all outline-none"
                placeholder="e.g., 55000"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Ranking
              </label>
              <input
                type="number"
                name="ranking"
                value={formData.ranking || ''}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-4 focus:ring-green-100 transition-all outline-none"
                placeholder="e.g., 5"
              />
            </div>
          </div>
        </div>

        {/* Test Scores */}
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-4">Test Scores</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                SAT Range
              </label>
              <input
                type="text"
                name="sat_range"
                value={formData.sat_range}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-4 focus:ring-green-100 transition-all outline-none"
                placeholder="e.g., 1420-1570"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                ACT Range
              </label>
              <input
                type="text"
                name="act_range"
                value={formData.act_range}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-4 focus:ring-green-100 transition-all outline-none"
                placeholder="e.g., 32-35"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Rating
              </label>
              <input
                type="text"
                name="rating"
                value={formData.rating}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-4 focus:ring-green-100 transition-all outline-none"
                placeholder="e.g., A+"
              />
            </div>
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Description
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={4}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-4 focus:ring-green-100 transition-all outline-none"
            placeholder="Brief description of the college..."
          />
        </div>

        {/* Majors & Features */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Majors (comma-separated)
            </label>
            <textarea
              value={majorsInput}
              onChange={(e) => setMajorsInput(e.target.value)}
              rows={4}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-4 focus:ring-green-100 transition-all outline-none"
              placeholder="e.g., Computer Science, Business, Engineering"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Features (comma-separated)
            </label>
            <textarea
              value={featuresInput}
              onChange={(e) => setFeaturesInput(e.target.value)}
              rows={4}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-4 focus:ring-green-100 transition-all outline-none"
              placeholder="e.g., Research opportunities, Study abroad programs"
            />
          </div>
        </div>

        {/* Options */}
        <div>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              name="direct_admission"
              checked={formData.direct_admission}
              onChange={handleChange}
              className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
            />
            <span className="text-sm font-semibold text-gray-700">Direct Admission Available</span>
          </label>
        </div>

        {/* Images (ImageKit) */}
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-1">Images</h2>
          <p className="text-sm text-gray-500 mb-4">
            Upload to ImageKit first, then paste the path or full URL here.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Hero Image URL</label>
              <input
                type="text"
                name="hero_url"
                value={formData.hero_url || ''}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-4 focus:ring-green-100 transition-all outline-none"
                placeholder="colleges/heroes/slug.jpg"
                data-testid="college-hero-url"
              />
              <p className="text-xs text-gray-500 mt-1">Upload to ImageKit first, then paste the path here.</p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Logo Image URL</label>
              <input
                type="text"
                name="logo_url"
                value={formData.logo_url || ''}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-4 focus:ring-green-100 transition-all outline-none"
                placeholder="colleges/logos/slug.png"
                data-testid="college-logo-url"
              />
              <p className="text-xs text-gray-500 mt-1">Upload to ImageKit first, then paste the path here.</p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Banner Image URL</label>
              <input
                type="text"
                name="banner_url"
                value={formData.banner_url || ''}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-4 focus:ring-green-100 transition-all outline-none"
                placeholder="colleges/banners/slug.jpg"
                data-testid="college-banner-url"
              />
              <p className="text-xs text-gray-500 mt-1">Upload to ImageKit first, then paste the path here.</p>
            </div>
          </div>
        </div>

        {/* Social Links */}
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Social Links</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">YouTube URL</label>
              <input
                type="url"
                name="youtube"
                value={formData.youtube || ''}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-4 focus:ring-green-100 transition-all outline-none"
                placeholder="https://youtube.com/@college"
                data-testid="college-youtube"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Instagram URL</label>
              <input
                type="url"
                name="instagram"
                value={formData.instagram || ''}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-4 focus:ring-green-100 transition-all outline-none"
                placeholder="https://instagram.com/college"
                data-testid="college-instagram"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Twitter / X URL</label>
              <input
                type="url"
                name="twitter"
                value={formData.twitter || ''}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-4 focus:ring-green-100 transition-all outline-none"
                placeholder="https://twitter.com/college"
                data-testid="college-twitter"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Facebook URL</label>
              <input
                type="url"
                name="facebook"
                value={formData.facebook || ''}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-4 focus:ring-green-100 transition-all outline-none"
                placeholder="https://facebook.com/college"
                data-testid="college-facebook"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">LinkedIn URL</label>
              <input
                type="url"
                name="linkedin"
                value={formData.linkedin || ''}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-4 focus:ring-green-100 transition-all outline-none"
                placeholder="https://linkedin.com/school/college"
                data-testid="college-linkedin"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Virtual Tour URL</label>
              <input
                type="url"
                name="virtual_tour_url"
                value={formData.virtual_tour_url || ''}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-4 focus:ring-green-100 transition-all outline-none"
                placeholder="https://college.edu/tour"
                data-testid="college-virtual-tour"
              />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-3">
            The College Website URL is stored separately under the existing Website field — leave it as-is.
          </p>
        </div>

        {/* Settings */}
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Settings</h2>
          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="is_featured"
                checked={!!formData.is_featured}
                onChange={handleChange}
                className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
                data-testid="college-is-featured"
              />
              <span className="text-sm font-semibold text-gray-700">Show on homepage carousel</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="is_partner"
                checked={!!formData.is_partner}
                onChange={handleChange}
                className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
                data-testid="college-is-partner"
              />
              <span className="text-sm font-semibold text-gray-700">EnrollForge partner college</span>
            </label>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-4 pt-6 border-t border-gray-200">
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 px-8 py-3 bg-green-700 text-white rounded-xl hover:bg-green-800 disabled:bg-gray-400 transition-colors font-medium shadow-lg"
          >
            <Save size={20} />
            <span>{saving ? 'Saving...' : isEdit ? 'Update College' : 'Create College'}</span>
          </button>
          <Link
            to="/admin/colleges"
            className="flex items-center gap-2 px-8 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
          >
            <X size={20} />
            <span>Cancel</span>
          </Link>
        </div>
      </form>
    </div>
  );
};

export default CollegeForm;
